import {
  Brain,
  FileText,
  Folder,
  Sun,
  Users,
  type LucideIcon,
} from "lucide-react";

export type NavItem = {
  /**
   * Relative to `/projects/:projectId`. `""` is that project's Today.
   *
   * Relative on purpose: React Router resolves it against the route that
   * *renders* the link, which is `ProjectLayout` at `projects/:projectId`. So
   * the sidebar never builds a path by string concatenation and never has to
   * know the current project id.
   */
  to: string;
  /**
   * Extra first URL segments that belong to this section.
   *
   * The topbar matches the first segment *exactly*. It used to use
   * `pathname.startsWith(to)`, which is why `/project/setup/2` inherited the
   * Project header — an accident that happened to be right. Declaring the
   * relationship here makes it deliberate, and stops a future `/journal-archive`
   * from being silently swallowed by `/journal`.
   */
  matches?: readonly string[];
  /** Passed to NavLink. Only Today needs it, being the empty path. */
  end?: boolean;
  label: string;
  /** Topbar eyebrow for this section, given the project's name. */
  eyebrow: (projectName: string) => string;
  title: string;
  /** When true, the sidebar shows the open-compliance-flag count. */
  showsAlertBadge?: boolean;
};

/**
 * The sidebar sections, in order.
 *
 * The source design's `tabs` array is Today / Journal / Brain / Project — it
 * renders no Crew entry, even though the Crew screen and the badge markup
 * (`key === "crew"`) both exist in the same file. That looks like an oversight
 * in the mockup rather than an intent to hide the roster, so Crew is included
 * here, second, where its alert badge is visible.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    to: "",
    end: true,
    label: "Today",
    eyebrow: (p) => `${p} · daily hub`,
    title: "Today",
  },
  {
    to: "crew",
    label: "Crew",
    eyebrow: (p) => `${p} · roster`,
    title: "Crew",
    showsAlertBadge: true,
  },
  {
    to: "journal",
    label: "Journal",
    eyebrow: (p) => `${p} · daily record`,
    title: "Journal",
  },
  {
    to: "brain",
    // The review after an import is about the project's files, so it sits
    // under the Brain, though its address is not under `brain/` (Phase 3f).
    matches: ["brain", "imports"],
    label: "Brain",
    eyebrow: (p) => `${p} · knowledge base`,
    title: "Brain",
  },
  {
    to: "overview",
    // Setup lives under the Project section: it is the same subject, and the
    // wizard has no nav entry of its own to highlight.
    matches: ["overview", "setup"],
    label: "Project",
    eyebrow: (p) => p,
    title: "Project",
  },
];

const ICONS: Record<string, LucideIcon> = {
  "": Sun,
  crew: Users,
  journal: FileText,
  brain: Brain,
  overview: Folder,
};

/** The icon for a nav item, keyed by its path. */
export function navIcon(to: string): LucideIcon {
  return ICONS[to] ?? Folder;
}

/**
 * The nav item owning a URL, given the path *below* `/projects/:projectId`.
 *
 * Exact first-segment matching, not prefix matching — see `NavItem.matches`.
 */
export function navItemForPath(rest: string): NavItem | undefined {
  const segment = rest.replace(/^\//, "").split("/")[0] ?? "";
  return NAV_ITEMS.find((n) => (n.matches ?? [n.to]).includes(segment));
}
